// src/contexts/AuthContext.js

import React, { createContext, useState, useEffect, useContext } from 'react';
import apiFetch from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blestaLinked, setBlestaLinked] = useState(null);
  const [blestaServices, setBlestaServices] = useState([]);

  const fetchBlestaStatus = async () => {
    try {
      const response = await apiFetch('/api/blesta/link-status', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setBlestaLinked(data.linked || false);
        setBlestaServices(data.services || []);
      } else {
        setBlestaLinked(false);
        setBlestaServices([]);
      }
    } catch (err) {
      console.error('Error fetching Blesta status:', err);
      setBlestaLinked(false);
      setBlestaServices([]);
    }
  };

  const refreshBlestaStatus = async () => {
    await fetchBlestaStatus();
  };

  const fetchUser = async () => {
    console.log('Fetching user data...');
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiFetch('/api/user', {
        credentials: 'include',
      });

      if (response.ok) {
        const userData = await response.json();
        console.log('User data fetched:', userData);
        setUser(userData); // Backend now returns user object directly, not { user: {...} }
        // After successful user fetch, also fetch Blesta link status
        fetchBlestaStatus();
      } else if (response.status === 401) {
        console.log('User not authenticated');
        setUser(null);
      } else {
        throw new Error('Failed to fetch user');
      }
    } catch (err) {
      console.error('Error fetching user:', err);
      setError(err.message);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await apiFetch('/logout', {
        method: 'POST',
        credentials: 'include',
      });
      setUser(null);
      setBlestaLinked(null);
      setBlestaServices([]);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  useEffect(() => {
    fetchUser();

    // Safety timeout: if auth check hangs for 15 seconds, stop loading
    const timeout = setTimeout(() => {
      setIsLoading((current) => {
        if (current) {
          console.error('Auth check timed out');
          setError('Authentication check timed out');
          setUser(null);
          return false;
        }
        return current;
      });
    }, 15000);

    return () => clearTimeout(timeout);
  }, []);

  const value = {
    user,
    setUser,
    isLoading,
    error,
    fetchUser,
    logout,
    blestaLinked,
    blestaServices,
    refreshBlestaStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
